import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { baoMatRepository } from '../../../lib/repositories/bao-mat.repository';
import { nguoiDungRepository } from '../../../lib/repositories/nguoi-dung.repository';
import { useToast } from '../../../lib/toast';
import { useTenantCuaToi } from '../../auth/hooks/useTenantCuaToi';
import { KHOA_EMAIL_DOMAIN } from '../nghiep-vu/cai-dat';
import { KHOA_NGUOI_DUNG, useKhiDoiBatBuocMfa } from '../../nguoi-dung/hooks/useNguoiDung';
import type { MfaRequiredUsersUpdate } from '../../nguoi-dung/types';
import type {
  BaoCaoIpMonitor,
  DanhMucCaiDat,
  DongChanIp,
  GhiCaiDatVao,
  MaGoi,
  SecurityPolicyView,
  PhamViChan,
  ThemChanVao,
  TrangPhienBan,
  TrangThongBao,
} from '../types';

/**
 * Một tài liệu chính sách: tầng tenant, tầng nền tảng, một gói, hoặc riêng năm
 * khoá mật khẩu. Bốn thứ cùng hình dạng (`SecurityPolicyView`, `If-Match`), nên
 * form cài đặt viết một lần và nhận "nguồn" này.
 */
export type NguonChinhSach =
  | { loai: 'tenant'; id: string }
  | { loai: 'nen-tang' }
  | { loai: 'goi'; ma: MaGoi }
  | { loai: 'mat-khau' };

export function khoaChinhSach(n: NguonChinhSach): readonly unknown[] {
  switch (n.loai) {
    case 'tenant':
      return ['bao-mat', 'tenant', n.id];
    case 'goi':
      return ['bao-mat', 'goi', n.ma];
    default:
      return ['bao-mat', n.loai];
  }
}

/**
 * Chính sách bảo mật vừa đổi ở nơi khác (`tenant.changed`, `reason: security`
 * của kênh sự kiện): tải lại chính sách tầng tenant và lịch sử thay đổi nếu
 * màn đang mở. Form đang sửa dở không mất bản nháp — lần lưu sau sẽ nhận
 * `412 VERSION_MISMATCH` và dải báo xung đột.
 */
export function lamMoiChinhSachBaoMat(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'bao-mat' && (q.queryKey[1] === 'tenant' || q.queryKey[1] === 'lich-su'),
  });
}

function docChinhSach(n: NguonChinhSach): Promise<SecurityPolicyView> {
  switch (n.loai) {
    case 'tenant':
      return baoMatRepository.tenant(n.id);
    case 'goi':
      return baoMatRepository.goi(n.ma);
    case 'nen-tang':
      return baoMatRepository.nenTang();
    case 'mat-khau':
      return baoMatRepository.matKhau();
  }
}

function ghiChinhSach(n: NguonChinhSach, ifMatch: number, vao: GhiCaiDatVao) {
  switch (n.loai) {
    case 'tenant':
      return baoMatRepository.ghiTenant(n.id, ifMatch, vao);
    case 'goi':
      return baoMatRepository.ghiGoi(n.ma, ifMatch, vao);
    case 'nen-tang':
      return baoMatRepository.ghiNenTang(ifMatch, vao);
    case 'mat-khau':
      return baoMatRepository.ghiMatKhau(ifMatch, vao);
  }
}

/**
 * Danh mục tham số. `staleTime: Infinity` — danh mục chỉ đổi khi deploy, giống
 * danh mục dịch vụ ở `useGoiDichVu`.
 */
export function useDanhMucCaiDat() {
  return useQuery<DanhMucCaiDat>({
    queryKey: ['bao-mat', 'danh-muc'],
    queryFn: baoMatRepository.danhMuc,
    staleTime: Infinity,
  });
}

export function useChinhSach(n: NguonChinhSach, bat = true) {
  return useQuery<SecurityPolicyView>({
    queryKey: khoaChinhSach(n),
    queryFn: () => docChinhSach(n),
    staleTime: 30_000,
    enabled: bat,
  });
}

/**
 * Ghi một chính sách với `If-Match`.
 *
 * Thành công thì đặt thẳng kết quả vào cache (API trả `SecurityPolicyView` mới,
 * không cần hỏi lại) và vô hiệu hoá lịch sử. **Không** toast lỗi ở đây: lỗi
 * theo trường và `VERSION_MISMATCH` hiện ngay trên form, cạnh chỗ sai.
 *
 * Ghi tầng nền tảng hay tầng gói đổi giá trị **kế thừa** của mọi tenant, nên
 * vô hiệu hoá cả nhánh `['bao-mat']`.
 */
export function useGhiChinhSach(n: NguonChinhSach) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bien: { ifMatch: number; vao: GhiCaiDatVao }) =>
      ghiChinhSach(n, bien.ifMatch, bien.vao),
    onSuccess: (view) => {
      qc.setQueryData(khoaChinhSach(n), view);
      if (n.loai === 'tenant') {
        void qc.invalidateQueries({ queryKey: ['bao-mat', 'lich-su', n.id] });
      } else {
        void qc.invalidateQueries({
          queryKey: ['bao-mat'],
          predicate: (q) => q.queryKey[1] !== 'danh-muc',
        });
      }
    },
  });
}

/** Lịch sử thay đổi của tầng tenant — phân trang con trỏ. */
export function useLichSuCaiDat(tenantId: string) {
  return useInfiniteQuery<TrangPhienBan>({
    queryKey: ['bao-mat', 'lich-su', tenantId],
    queryFn: ({ pageParam }) => baoMatRepository.lichSu(tenantId, pageParam as string | null),
    initialPageParam: null,
    getNextPageParam: (trang) => trang.next_cursor,
    staleTime: 30_000,
  });
}

export function useKhoiPhucPhienBan(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bien: { version: number; ifMatch: number }) =>
      baoMatRepository.khoiPhuc(tenantId, bien.version, bien.ifMatch),
    onSuccess: (view) => {
      qc.setQueryData(khoaChinhSach({ loai: 'tenant', id: tenantId }), view);
      void qc.invalidateQueries({ queryKey: ['bao-mat', 'lich-su', tenantId] });
    },
  });
}

// ── Xác thực hai lớp: mức cả tổ chức và mức từng người (ADR-0047) ──

/** Khoá của mức cả tổ chức trong cấu hình ba tầng (ADR-0059 mục 1.3). */
export const KHOA_MFA_TO_CHUC = 'auth.mfa_required';

/**
 * Tenant đang bắt buộc xác thực hai lớp cho cả tổ chức không — giá trị **hiệu
 * lực** của `auth.mfa_required` ở tầng tenant (đã tính tầng gói, nền tảng).
 * `undefined` khi chưa đọc được hoặc người xem không có ngữ cảnh tenant.
 *
 * Mức cả tổ chức chỉ có một nguồn sự thật là cấu hình ba tầng (ADR-0059); cờ
 * riêng của từng người là role gán thẳng ở Keycloak (ADR-0047). Hai mức độc
 * lập: hiệu lực của một người là mức tổ chức **hoặc** cờ riêng.
 */
export function useMfaCaToChuc(): boolean | undefined {
  const { tenantId } = useTenantCuaToi();
  const q = useChinhSach({ loai: 'tenant', id: tenantId ?? '' }, Boolean(tenantId));
  const muc = q.data?.settings.find((m) => m.key === KHOA_MFA_TO_CHUC);
  return muc === undefined ? undefined : muc.value === true;
}

/**
 * Chính sách email đăng nhập của tổ chức đang đăng nhập (ADR-0099), cho hộp mời người dùng:
 *
 * - `emailNgoaiDomain`: tổ chức tắt `auth.chi_email_domain_xac_minh`, mời được email bất kỳ chưa thuộc tổ chức khác.
 *   `undefined` khi đang tải, tải lỗi hoặc người xem không có ngữ cảnh tenant — hộp mời khi đó nói câu chung, backend
 *   vẫn kiểm.
 * - `domains`: domain đã xác minh, để nhắc khi mời email ngoài các domain này.
 */
export function useChinhSachEmailToChuc(): { emailNgoaiDomain: boolean | undefined; domains: string[] | undefined } {
  const { tenantId } = useTenantCuaToi();
  const q = useChinhSach({ loai: 'tenant', id: tenantId ?? '' }, Boolean(tenantId));
  const muc = q.data?.settings.find((m) => m.key === KHOA_EMAIL_DOMAIN);
  return {
    emailNgoaiDomain: muc === undefined ? undefined : muc.value === false,
    domains: q.data?.verified_domains,
  };
}

/**
 * Một lần lưu bộ chọn ba mức (`nghiep-vu/muc-mfa.ts`, `keHoachLuu`): phần nào
 * `null` thì không gửi.
 */
export interface LuuMucMfa {
  /** `PUT /users/mfa-required` — thay cả tập cờ riêng (ADR-0047). */
  danhSach: MfaRequiredUsersUpdate | null;
  /**
   * `PUT /tenants/{id}/security` chỉ với khoá `auth.mfa_required`, `If-Match`
   * theo phiên bản chính sách đang xem. `apDungNgay` thành `mfa_apply: NOW`
   * (huỷ mọi phiên của tenant) khi bật bắt buộc cả tổ chức.
   */
  chinhSach: { ifMatch: number; toChuc: boolean; apDungNgay: boolean } | null;
}

/**
 * Lỗi của lần lưu ba mức. `daLuuDanhSach` là danh sách người đã lưu xong
 * nhưng chính sách tổ chức hỏng (`412`, `400`…) — màn phải nói rõ lưu nửa
 * chừng, và giữ lựa chọn để lưu lại phần còn thiếu.
 */
export type LoiLuuMucMfa = ApiError & { daLuuDanhSach?: boolean };

/**
 * Lưu bộ chọn ba mức xác thực hai lớp (ADR-0047 trên nền ADR-0059): danh sách
 * **trước**, chính sách tổ chức **sau** — thu hẹp từ "cả tổ chức" về "người
 * được chọn" mà làm ngược lại thì có một khoảng người được chọn cũng không bị
 * buộc OTP.
 *
 * Danh sách lưu xong là ghi ngay vào cache, nên chính sách hỏng sau đó thì
 * bộ chọn so với tập mới và lần lưu lại chỉ còn gửi chính sách. Lỗi **không**
 * bật toast: view đặt cạnh danh sách hoặc cạnh nút lưu.
 */
export function useLuuMucMfa(tenantId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const khiDoiDanhSach = useKhiDoiBatBuocMfa();

  return useMutation<void, LoiLuuMucMfa, LuuMucMfa>({
    mutationFn: async ({ danhSach, chinhSach }) => {
      if (danhSach) khiDoiDanhSach(await nguoiDungRepository.datBatBuocMfa(danhSach));
      if (!chinhSach) return;
      try {
        const view = await baoMatRepository.ghiTenant(tenantId, chinhSach.ifMatch, {
          settings: { [KHOA_MFA_TO_CHUC]: chinhSach.toChuc },
          ...(chinhSach.toChuc ? { mfa_apply: chinhSach.apDungNgay ? 'NOW' : 'NEXT_LOGIN' } : {}),
        });
        qc.setQueryData(khoaChinhSach({ loai: 'tenant', id: tenantId }), view);
        void qc.invalidateQueries({ queryKey: ['bao-mat', 'lich-su', tenantId] });
      } catch (loi) {
        throw { ...(loi as ApiError), daLuuDanhSach: danhSach !== null } satisfies LoiLuuMucMfa;
      }
    },
    // `503` của `PUT /users/mfa-required` để lại tập ghi dở (thêm trước, gỡ sau —
    // ADR-0047 mục 4): đọc lại tập thật để bộ chọn so với đúng cái đã ghi.
    onError: (loi, { danhSach }) => {
      if (danhSach && !loi.daLuuDanhSach && loi.status === 503) {
        void qc.invalidateQueries({ queryKey: KHOA_NGUOI_DUNG.batBuocMfa });
      }
    },
    onSuccess: (_kq, { danhSach, chinhSach }) => {
      toast.success(
        t(
          chinhSach?.toChuc && chinhSach.apDungNgay
            ? 'baoMat.mfa.daLuuVaDangXuat'
            : danhSach?.apply_now
              ? 'baoMat.mfa.daLuuVaDangXuatNguoiChon'
              : 'baoMat.mfa.daLuu',
        ),
      );
    },
  });
}

/** IP ngoài dải tin cậy trong 72 giờ qua khi tổ chức ở mức Chỉ ghi nhận (ADR-0090 mục 1). */
export function useIpMonitor(tenantId: string) {
  return useQuery<BaoCaoIpMonitor>({
    queryKey: ['bao-mat', 'ip-monitor', tenantId],
    queryFn: () => baoMatRepository.ipMonitor(tenantId, 72),
    staleTime: 60_000,
  });
}

// ── Blocklist của nền tảng và của tenant (ADR-0059 mục 6.4, ADR-0090 mục 3) ──

const khoaChan = (phamVi: PhamViChan) =>
  phamVi.loai === 'tenant' ? ['bao-mat', 'blocklist', 'tenant', phamVi.tenantId] : ['bao-mat', 'blocklist', 'nen-tang'];

/**
 * Danh sách chặn. Làm mới mỗi phút khi đang mở màn: dòng chặn tự động có hạn 15 phút, và cột "Còn lại" phải theo kịp
 * việc server dọn dòng đã hết hạn.
 */
export function useDanhSachChan(phamVi: PhamViChan) {
  return useQuery<{ items: DongChanIp[] }>({
    queryKey: khoaChan(phamVi),
    queryFn: () => baoMatRepository.danhSachChan(phamVi),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useThemChan(phamVi: PhamViChan) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bien: { vao: ThemChanVao; khoaLap: string }) =>
      baoMatRepository.themChan(phamVi, bien.vao, bien.khoaLap),
    onSuccess: () => void qc.invalidateQueries({ queryKey: khoaChan(phamVi) }),
  });
}

export function useXoaChan(phamVi: PhamViChan) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => baoMatRepository.xoaChan(phamVi, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: khoaChan(phamVi) }),
  });
}

// ── Thông báo ──

export function useGuiThu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notifyKey: string) => baoMatRepository.guiThu(notifyKey),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['bao-mat', 'thong-bao'] }),
  });
}

export function useThongBaoGanDay() {
  return useInfiniteQuery<TrangThongBao>({
    queryKey: ['bao-mat', 'thong-bao'],
    queryFn: ({ pageParam }) => baoMatRepository.thongBao(pageParam as string | null),
    initialPageParam: null,
    getNextPageParam: (trang) => trang.next_cursor,
    staleTime: 30_000,
  });
}
